import {
    deleteYoutubeInfluencerPost,
    getAllYoutubeInfluencerPosts,
    getPostPaymentRequests,
    getYoutubeInfluencerPostGroupLiveStats,
    getYouTubeInfluencerPosts,
    patchYoutubeInfluencerPost,
    PostPaymentRequest,
    postYoutubeInfluencerPost,
    youtube,
    YoutubeInfluencerPostBody,
    YoutubeInfluencerPostPatchData,
} from '@round/api';
import useNonNullContext from 'Hooks/useNonNullContext';
import { uniq } from 'lodash';
import { YoutubeInfluencerPostTableRow } from 'Modules/Advertising/InfluencerPlan/features/YoutubeTable/components/YoutubeInfluencerPostsTable/YoutubeInfluencerPostsTable';
import { useCallback, useContext, useMemo } from 'react';
import { isNotNullOrUndefined, isNumber } from 'utility/utility';
import { InfluencerPlanContext } from '../../InfluencerPlanContext';
import { YoutubeCreatorsStateContext, YoutubeCreatorsDispatchContext } from '../YoutubeCreatorsContext';
import { useCheckUserGroupsAccess } from 'Modules/Auth/hooks/useCheckUserGroupsAccess';
import { useAllCreatorbasePostsByPostIds } from '@round/creatorbase-helpers/api-hooks';

export default function useYoutubeInfluencerPosts() {
    const userHasPaymentsAccess = useCheckUserGroupsAccess(['round_planner', 'round_finance'], 'any');

    const { influencerPlan } = useContext(InfluencerPlanContext);
    const state = useNonNullContext(YoutubeCreatorsStateContext);
    const dispatch = useNonNullContext(YoutubeCreatorsDispatchContext);

    const influencerPostIds = (state.youtubeInfluencerPosts?.map((p) => p.influencer_post_id) ?? []).filter(
        isNotNullOrUndefined
    );

    const { data: youtubeCreatorbasePosts = [] } = useAllCreatorbasePostsByPostIds(influencerPostIds, {
        enabled: Boolean(influencerPostIds.length > 0 && userHasPaymentsAccess),
    });

    const fetchData = useCallback(
        async (requestInit?: RequestInit) => {
            if (!influencerPlan) {
                return;
            }

            try {
                const influencerPosts = await getAllYoutubeInfluencerPosts({ plan_id: influencerPlan.id }, requestInit);
                const videosToFetch = uniq(influencerPosts.map((post) => post.video_id).filter(isNumber));
                const paymentRequestIds = influencerPosts.map((post) => post.payment_request_id).filter(isNumber);

                const [videos, paymentRequests] = await Promise.allSettled([
                    videosToFetch.length
                        ? youtube
                              .getYoutubeVideos(
                                  { id: videosToFetch.join(','), page_size: videosToFetch.length },
                                  requestInit
                              )
                              .then((response) => (response.status === 200 ? response.data.results : []))
                        : Promise.resolve([]),
                    paymentRequestIds.length && userHasPaymentsAccess
                        ? getPostPaymentRequests(
                              { id: paymentRequestIds.join(','), page_size: paymentRequestIds.length },
                              requestInit
                          ).then((response) => (response.status === 200 ? response.data.results : []))
                        : Promise.resolve([]),
                ]);

                dispatch({
                    type: 'setIsYoutubeInfluencerPostsInitialized',
                    payload: {
                        youtubeInfluencerPosts: influencerPosts,
                        youtubeVideos: videos.status === 'fulfilled' ? videos.value : [],
                        youtubePaymentRequests: paymentRequests.status === 'fulfilled' ? paymentRequests.value : [],
                    },
                });
            } catch (e) {
                if (e instanceof Error && e.name === 'AbortError') {
                    throw e;
                }

                dispatch({
                    type: 'setYoutubeInfluencerPostsLoadingError',
                    payload: "Sorry, we're experiencing technical issues",
                });

                throw e;
            }
        },
        [dispatch, influencerPlan, userHasPaymentsAccess]
    );

    const createPost = useCallback(
        async (data: Omit<YoutubeInfluencerPostBody, 'plan_id'>) => {
            if (!influencerPlan) {
                return;
            }

            const response = await postYoutubeInfluencerPost({ ...data, plan_id: influencerPlan.id });
            if (response.status !== 201) {
                return response;
            }

            if (response.data.group_id) {
                await getYoutubeInfluencerPostGroupLiveStats({
                    id: response.data.group_id.toString(),
                })
                    .then((groupStatsResponse) => {
                        if (groupStatsResponse.status === 200) {
                            dispatch({
                                type: 'setIsYoutubeInfluencerPostGroupStatsInitialized',
                                payload: (
                                    state.youtubeInfluencerPostGroupStats?.filter(
                                        (stat) => stat.group_id !== groupStatsResponse.data[0].group_id
                                    ) ?? []
                                ).concat(groupStatsResponse.data[0]),
                            });
                        }
                    })
                    .catch(() => {
                        //noop
                    });
            }

            let video: youtube.YoutubeVideo | null = null;
            if (response.data.video_id) {
                await youtube
                    .getYoutubeVideos({ id: response.data.video_id.toString(), page_size: 1 })
                    .then((videoResponse) => {
                        video = videoResponse?.status === 200 ? videoResponse.data.results[0] ?? null : null;
                    })
                    .catch(() => {
                        //noop
                    });
            }

            dispatch({ type: 'youtubeInfluencerPostCreated', payload: { post: response.data, video } });
            dispatch({ type: 'resetYoutubeInfluencerPlanStats' });
            return response;
        },
        [dispatch, influencerPlan, state.youtubeInfluencerPostGroupStats]
    );

    const deletePost = useCallback(
        async (postId: number) => {
            const response = await deleteYoutubeInfluencerPost(postId);
            if (response.status === 204) {
                dispatch({ type: 'youtubeInfluencerPostDeleted', payload: postId });
            }

            return response;
        },
        [dispatch]
    );

    const updatePost = useCallback(
        async (postId: number, data: Partial<YoutubeInfluencerPostPatchData>) => {
            const currentVideoId = state.youtubeInfluencerPosts?.find((post) => post.id === postId)?.video_id;

            const response = await patchYoutubeInfluencerPost(postId, data);
            if (response.status !== 200) {
                return response;
            }

            if (response.data.video_id === currentVideoId) {
                dispatch({ type: 'youtubeInfluencerPostUpdated', payload: { post: response.data } });
                return response;
            }

            if (response.data.group_id) {
                await getYoutubeInfluencerPostGroupLiveStats({
                    id: response.data.group_id.toString(),
                })
                    .then((groupStatsResponse) => {
                        if (groupStatsResponse.status === 200) {
                            dispatch({
                                type: 'setIsYoutubeInfluencerPostGroupStatsInitialized',
                                payload: (
                                    state.youtubeInfluencerPostGroupStats?.filter(
                                        (stat) => stat.group_id !== groupStatsResponse.data[0].group_id
                                    ) ?? []
                                ).concat(groupStatsResponse.data[0]),
                            });
                        }
                    })
                    .catch(() => {
                        //noop
                    });
            }

            let video: youtube.YoutubeVideo | null = null;

            if (response.data.video_id) {
                await youtube
                    .getYoutubeVideos({ id: response.data.video_id.toString(), page_size: 1 })
                    .then((videoResponse) => {
                        if (videoResponse.status === 200) {
                            video = videoResponse.data.results[0] ?? null;
                        }
                    })
                    .catch(() => {
                        //noop
                    });
            }

            dispatch({ type: 'youtubeInfluencerPostUpdated', payload: { post: response.data, video } });
            return response;
        },
        [dispatch, state.youtubeInfluencerPosts, state.youtubeInfluencerPostGroupStats]
    );

    const refreshPost = useCallback(
        async (postId: number) => {
            const response = await getYouTubeInfluencerPosts({ id: postId.toString(), page_size: 1 });
            if (response.status === 200) {
                const post = response.data.results[0] || null;

                if (post) {
                    dispatch({ type: 'youtubeInfluencerPostUpdated', payload: { post } });
                }
            }
            return response;
        },
        [dispatch]
    );

    const refreshYoutubeInfluencerPostsForGroup = useCallback(
        async (groupId: number, requestInit?: RequestInit) => {
            const postIds = state.youtubeInfluencerPosts?.filter((p) => p.group_id === groupId).map((p) => p.id) ?? [];
            if (!postIds.length) {
                return;
            }

            dispatch({ type: 'setRefreshingGroupId', payload: groupId });

            try {
                const groupPosts = await getAllYoutubeInfluencerPosts({ id: postIds.join(',') }, requestInit);
                for (const post of groupPosts) {
                    dispatch({ type: 'youtubeInfluencerPostUpdated', payload: { post } });
                }
            } finally {
                dispatch({ type: 'setRefreshingGroupId', payload: null });
            }
        },
        [dispatch, state.youtubeInfluencerPosts]
    );

    const addPaymentRequest = useCallback(
        (paymentRequest: PostPaymentRequest) => {
            dispatch({
                type: 'addPaymentRequest',
                payload: paymentRequest,
            });
        },
        [dispatch]
    );

    const data: YoutubeInfluencerPostTableRow[] = useMemo(
        () =>
            state.youtubeInfluencerPosts?.map((post) => {
                const video = state.youtubeVideos?.find((video) => video.id === post.video_id);
                const paymentRequest =
                    state.youtubePaymentRequests?.find(
                        (paymentRequest) => paymentRequest.id === post.payment_request_id
                    ) ?? null;
                const creatorbasePost = youtubeCreatorbasePosts.find((p) => p.id === post.influencer_post_id) ?? null;

                return { ...post, video, paymentRequest, creatorbasePost };
            }) ?? [],
        [state.youtubeInfluencerPosts, state.youtubeVideos, state.youtubePaymentRequests, youtubeCreatorbasePosts]
    );

    return {
        isLoading: state.isYouTubeInfluencerPostsLoading,
        isInitialized: state.isYouTubeInfluencerPostsInitialized,
        error: state.youtubeInfluencerPostsLoadingError,
        influencerPosts: data,
        refreshingGroupId: state.refreshingGroupId,
        refreshYoutubeInfluencerPostsForGroup,

        fetchData,
        createPost,
        deletePost,
        updatePost,
        refreshPost,
        addPaymentRequest,
    };
}
